import React, { useCallback, useContext, useMemo, useState } from 'react';

import { Form } from 'react-final-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { FORM_ERROR } from 'final-form';

import verificationBGImage from '../../assets/images/amazon/verificationBGImage.png';
import TenantConfig from '../../config';
import links from '../../constants/links';
import { UserContext } from '../../context/UserContext';

/** helpers */
import {
  composeValidators,
  email,
  max,
  min,
  password,
  phone,
  required,
} from '../../helpers/validation';

/** components */
import { FormInput } from '../FormElements';

const Register = ({ onSubmit, className }) => {
  const { setLoading, width, landingHeaderBeakpoint } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const destination = query.get('app');
  const [userAccountConsentAccepted, setUserAccountConsentAccepted] = useState(false);
  const formOptions = useMemo(() => {
    return {
      formName: 'any',
      fields: [
        {
          label: 'First name',
          placeholder: 'Enter your first name',
          required: true,
          validate: composeValidators(
            required("First name can't be empty"),
            max('Maximum length is 50 characters', 50)
          ),
          name: 'firstname',
        },
        {
          label: 'Last name',
          placeholder: 'Enter your last name',
          name: 'lastname',
          validate: composeValidators(
            required("Last name can't be empty"),
            max('Maximum length is 50 characters', 50)
          ),
          required: true,
        },
        {
          label: 'Email',
          placeholder: 'Enter your email address',
          required: true,
          type: 'email',
          validate: composeValidators(
            required("Email can't be empty"),
            email('Email is invalid'),
            max('Maximum length is 50 characters', 50)
          ),
          name: 'email',
        },
        {
          label: 'Password',
          placeholder: 'Choose a strong password',
          required: true,
          type: 'password',
          validate: composeValidators(
            required("Password can't be empty"),
            min('Password must be at least 8 characters long', 8),
            max('Maximum length is 64 characters', 64),
            password('Must have 8 characters, uppercase & lowercase letters, one number')
          ),
          name: 'password',
        },
        {
          label: 'Confirm Password',
          placeholder: 'Retype password',
          required: true,
          type: 'password',
          validate: composeValidators(
            required('You must confirm password'),
            max('Maximum length is 64 characters', 64)
          ),
          name: 'confirmpassword',
        },
        {
          label: 'Mobile Number',
          required: true,
          type: 'tel',
          name: 'phone',
          validate: composeValidators(
            max('Maximum length is 50 characters', 50),
            required("Phone can't be empty"),
            phone('Phone is invalid')
          ),
        },
      ],
    };
  }, []);

  const handleTermChecks = () => {
    setUserAccountConsentAccepted(!userAccountConsentAccepted);
  };

  const onSubmitInternal = useCallback(
    async (values) => {
      if (destination !== '/admin' && userAccountConsentAccepted === false) {
        const message = 'Please review and accept the above consent to proceed.';
        return { [FORM_ERROR]: message };
      } else {
        setLoading(true);
        try {
          await onSubmit(values);
          setLoading(false);
          navigate('/auth/phone-verification' + location.search);
        } catch (err) {
          setLoading(false);
          let message;
          const duplicated = err?.request?.response
            ? JSON.parse(err.request.response)?.errors.find((e) => e.code === 'DUPLICATED_ENTITY')
            : null;
          if (duplicated) {
            switch (duplicated?.info?.entity) {
              case 'EMAIL':
                message =
                  'Email already registered. Please try to Sign In or Recover your password!';
                break;
              case 'PHONE':
                message =
                  'Phone already registered. Please try to Sign In or Recover your password!';
                break;
            }
          }
          return { [FORM_ERROR]: message };
        }
      }
    },
    [navigate, location, userAccountConsentAccepted, onSubmit]
  );

  return (
    <>
      {className === 'admin' && (
        <div className="logo-wrapper">
          <img className="logo" src={TenantConfig.headerLogo} alt="logo" />
        </div>
      )}
      <div className={`${className === 'admin' ? 'admin-form' : className} section`}>
        <div className={`${className}-header`}>
          <h1 {...(className === 'admin' && { className: `${className}-header-title` })}>
            {className === 'admin' ? 'Sign Up' : 'Welcome! Sign Up!'}
          </h1>
        </div>
        {className === 'admin' && (
          <div className="admin-form__description-wrapper">
            <div className="admin-form__description">Please confirm your details</div>
            <div className="admin-divider" />
          </div>
        )}
        <div className={`${className}-main`}>
          <Form
            onSubmit={onSubmitInternal}
            validate={(values) => {
              const errors = {};
              if (values.password && values.confirmpassword !== values.password) {
                errors.confirmpassword = 'Passwords must match';
              }
              return errors;
            }}
            initialValues={{
              phone: '+1-___-___-___',
            }}
            render={({ handleSubmit, form, submitting, pristine, submitError }) => (
              <form onSubmit={handleSubmit} className="form">
                {formOptions.fields.map((f) => {
                  return className === 'admin' ? (
                    <div className={`admin-form__${f.name}`} key={`form_${f.name}`}>
                      <FormInput {...f} />
                    </div>
                  ) : (
                    <FormInput {...f} key={`form_${f.name}`} />
                  );
                })}
                {className === 'admin' ? (
                  <div className="form-buttons">
                    <div className="form-buttons-wrapper">
                      <button
                        className="btn primary-btn"
                        type="submit"
                        disabled={submitting || pristine}
                      >
                        Register
                      </button>
                    </div>

                    {submitError && <p className="error-text">{submitError}</p>}
                  </div>
                ) : (
                  <>
                    <div className="registerCustomCheckBox">
                      <label htmlFor={`terms-checks`}>
                        <input
                          type="checkbox"
                          id={`terms-checks`}
                          name="terms-checks"
                          onChange={() => handleTermChecks()}
                        />
                        <span>
                          I agree to{' '}
                          <a
                            href={links.globalLinks.TermsAndConditionLink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {links.globalLinks.TermsAndConditionText}
                          </a>
                        </span>
                      </label>
                    </div>
                    <button
                      className="btn primary-btn"
                      type="submit"
                      disabled={submitting || pristine}
                    >
                      Register
                    </button>
                    {submitError && <p className="error-text">{submitError}</p>}
                  </>
                )}
              </form>
            )}
          />
        </div>
        <p className={className === 'admin' ? 'admin-subtitle' : 'subtitle'}>
          <small>
            <Link to={`/auth/sign-in${location.search}`}>Already registered? Sign in!</Link>
          </small>
        </p>
      </div>

      {TenantConfig?.auth.renderBottomImg && className !== 'admin' && (
        <img
          className="phone-verification-bg"
          src={verificationBGImage}
          alt="Verification Background Image"
        />
      )}
    </>
  );
};

export default Register;
